import { getYoutubeVideoId } from 'volto-ministerio-publico-federal/helpers/Utils/Utils';

/**
 * Teto de vídeos na lateral.
 *
 * Seis chamadas preenchem a coluna de 4/12 até a altura do player: com cinco
 * sobrava um vão embaixo da lista. A partir da sétima a lista passaria do
 * player e a faixa deixaria de fechar no cartão.
 */
export const VIDEOS_LATERAIS_MAX = 6;

/** Um no player mais os seis da lateral. */
export const VIDEOS_MAX_ITENS = VIDEOS_LATERAIS_MAX + 1;

/** Título padrão da faixa, também usado como `placeholder` no formulário. */
export const VIDEOS_TITULO = 'Rádio e TV';

/** Item como o editor grava. */
export interface VideoItem {
  '@id'?: string;
  url?: string;
  titulo?: string;
  descricao?: string;
  /** Texto falado no vídeo, para quem não pode ou não quer assistir (e-MAG). */
  transcricao?: string;
}

/** Dado gravado pelo bloco. */
export interface VideosData {
  '@type'?: string;
  titulo?: string;
  exibirTitulo?: boolean;
  videos?: VideoItem[];
}

/** Item já resolvido para a tela. */
export interface VideoCard {
  key: string;
  videoId: string;
  titulo: string;
  descricao: string;
  transcricao: string;
  /** `maxresdefault`, que não existe para vídeo publicado fora de HD. */
  thumb: string;
  /** `hqdefault`, que existe sempre — usado no `onError` da imagem. */
  thumbFallback: string;
  /**
   * Índice na lista CRUA do bloco. É ele que o lápis do modo de edição manda
   * para o `ObjectListWidget`: sem guardá-lo, um item descartado por URL
   * inválida deslocaria todos os seguintes e o editor abriria o vídeo errado
   * (mesma trava de `Imprensa/helpers.ts#toContatosIndexados`).
   */
  sourceIndex: number;
}

/**
 * Thumbnail do vídeo no YouTube.
 *
 * Mesma dupla que a capa já usa (`View/CapaView/helpers.ts#youtubeThumb`):
 * pede-se a boa e troca-se pela garantida quando ela falha.
 */
export const youtubeThumb = (
  videoId: string,
  size: 'maxres' | 'high' = 'maxres',
): string =>
  `https://img.youtube.com/vi/${videoId}/${
    size === 'maxres' ? 'maxresdefault' : 'hqdefault'
  }.jpg`;

/**
 * `src` do player.
 *
 * `youtube-nocookie.com` é o domínio que o YouTube mantém sem cookie de
 * rastreio até o play — o mesmo que o `YoutubeDialog` da capa já usa. O
 * `autoplay=1` só faz sentido porque este iframe nunca é montado sem clique:
 * quem chega na página vê a fachada.
 */
export const youtubeEmbedSrc = (videoId: string): string =>
  `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;

/**
 * Converte a lista crua do editor nos cards da tela, descartando o que não dá
 * para exibir: item sem URL, ou com URL que não é do YouTube.
 */
export const toVideoCards = (videos?: VideoItem[]): VideoCard[] =>
  (videos ?? [])
    .map((video, index) => ({
      video,
      index,
      videoId: getYoutubeVideoId(video?.url ?? '')?.videoID ?? null,
    }))
    .filter(
      (entry): entry is typeof entry & { videoId: string } => !!entry.videoId,
    )
    .map(({ video, index, videoId }) => ({
      key: video['@id'] ?? `video-${index}`,
      videoId,
      titulo: video.titulo?.trim() ?? '',
      descricao: video.descricao?.trim() ?? '',
      transcricao: video.transcricao?.trim() ?? '',
      thumb: youtubeThumb(videoId),
      thumbFallback: youtubeThumb(videoId, 'high'),
      sourceIndex: index,
    }));

/**
 * Quantos itens o editor preencheu mas o `toVideoCards` recusou — vira aviso
 * na tela de edição, senão o vídeo simplesmente sumiria sem explicação.
 */
export const contarVideosInvalidos = (videos?: VideoItem[]): number =>
  (videos ?? []).length - toVideoCards(videos).length;
